import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArtifactSummary } from '../../types/artifact';
import { useTheme } from '../../contexts/ThemeContext';
import { FiExternalLink, FiCopy, FiCheck, FiClock } from 'react-icons/fi';
import { relativeTime } from '../../utils/relativeTime';

// ── Type maps ────────────────────────────────────────────────────────────────

const TYPE_ACCENT: Record<string, string> = {
  text:     '#3b82f6',
  code:     '#10b981',
  image:    '#ec4899',
  idea:     '#f59e0b',
  critique: '#ef4444',
  solution: '#6366f1',
  document: '#6b7280',
  diagram:  '#14b8a6',
  json:     '#f97316',
  yaml:     '#06b6d4',
  toml:     '#84cc16',
  csv:      '#8b5cf6',
  prompt:   '#ec4899',
};

const TYPE_ACCENT_DARK: Record<string, string> = {
  text:     '#1d4ed8',
  code:     '#047857',
  image:    '#be185d',
  idea:     '#b45309',
  critique: '#b91c1c',
  solution: '#4338ca',
  document: '#374151',
  diagram:  '#0f766e',
  json:     '#c2410c',
  yaml:     '#0e7490',
  toml:     '#4d7c0f',
  csv:      '#6d28d9',
  prompt:   '#be185d',
};

const TYPE_EMOJI: Record<string, string> = {
  text:     '📝',
  code:     '💻',
  image:    '🖼️',
  idea:     '💡',
  critique: '🔍',
  solution: '✅',
  document: '📄',
  diagram:  '📊',
  json:     '🔧',
  yaml:     '⚙️',
  toml:     '🔩',
  csv:      '🗃️',
  prompt:   '✨',
};

const TYPE_LABEL: Record<string, string> = {
  text:     'Text',
  code:     'Code',
  image:    'Image',
  idea:     'Idea',
  critique: 'Critique',
  solution: 'Solution',
  document: 'Document',
  diagram:  'Diagram',
  json:     'JSON',
  yaml:     'YAML',
  toml:     'TOML',
  csv:      'CSV',
  prompt:   'Prompt',
};

// Layer 1 — static type taglines, always available as a baseline
const TYPE_TAGLINE: Record<string, string> = {
  text:     'Plain text content',
  code:     'Source code',
  image:    'Image or visual asset',
  idea:     'Concept or brainstorm output',
  critique: 'Analysis and structured feedback',
  solution: 'Proposed solution or recommendation',
  document: 'Formatted document or report',
  diagram:  'Visual diagram or chart',
  json:     'Structured JSON data',
  yaml:     'YAML configuration or data',
  toml:     'TOML configuration file',
  csv:      'Tabular data (CSV)',
  prompt:   'LLM prompt template',
};

const PHASE_LABEL: Record<string, string> = {
  teamselection: 'Team Selection',
  brainstorming: 'Brainstorming',
  critique:      'Critique',
  synthesis:     'Synthesis',
  refinement:    'Refinement',
};

// Layer 2 — compose a readable sentence from existing summary fields
function composeDescription(artifact: ArtifactSummary): string {
  const parts: string[] = [];
  if (artifact.version > 1) parts.push(`v${artifact.version}`);
  if (artifact.phase) parts.push(PHASE_LABEL[artifact.phase] ?? artifact.phase);
  if (artifact.agent_name) parts.push(`by ${artifact.agent_name}`);
  const tags = ((artifact as any).tags ?? []) as string[];
  if (tags.length > 0) parts.push(tags.slice(0, 3).join(', '));
  return parts.join(' · ');
}

function getAccent(type: string): string {
  return TYPE_ACCENT[type] ?? '#6b7280';
}

function getAccentDark(type: string): string {
  return TYPE_ACCENT_DARK[type] ?? '#374151';
}

// Kept for potential future use but no longer used for card display
function getContentPreview(content: any): string {
  if (!content) return '';
  if (typeof content === 'string') return content.slice(0, 140);
  try { return JSON.stringify(content).slice(0, 140); } catch { return ''; }
}

// Agent initials avatar — mirrors PersonaAvatar pattern
function AgentAvatar({ name, accent }: { name: string; accent: string }) {
  const initials = name
    .split(' ')
    .map(p => p.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
  return (
    <div
      className="w-5 h-5 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
      style={{ backgroundColor: accent, fontSize: '9px' }}
    >
      {initials}
    </div>
  );
}

interface ArtifactCardProps {
  artifact: ArtifactSummary;
  onClick?: (artifactId: string) => void;
}

export const ArtifactCard: React.FC<ArtifactCardProps> = ({ artifact, onClick }) => {
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [hovered, setHovered] = useState(false);

  const accent     = getAccent(artifact.type);
  const accentDark = getAccentDark(artifact.type);
  const emoji      = TYPE_EMOJI[artifact.type]  ?? '📄';
  const typeLabel  = TYPE_LABEL[artifact.type]  ?? artifact.type;
  const tags       = ((artifact as any).tags ?? []) as string[];

  // Description layers
  const tagline        = TYPE_TAGLINE[artifact.type] ?? typeLabel;
  const composed       = composeDescription(artifact);
  const dbDescription  = artifact.description;

  const handleCardClick = () => { if (onClick) onClick(artifact.id); };

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/artifacts/${artifact.id}`);
  };

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    const text = typeof (artifact as any).content === 'string'
      ? (artifact as any).content
      : JSON.stringify((artifact as any).content ?? artifact.name);
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className={`
        relative rounded-xl border overflow-hidden cursor-pointer
        transition-all duration-300 ease-out
        hover:-translate-y-1 hover:scale-[1.02]
        ${darkMode
          ? 'bg-gray-800 border-gray-700 hover:border-gray-500'
          : 'bg-white border-gray-200 hover:border-gray-300'
        }
      `}
      style={{
        boxShadow: hovered
          ? darkMode
            ? `inset 3px 0 0 ${accent}, 0 20px 40px rgba(0,0,0,0.3), 0 0 0 1px ${accent}22`
            : `inset 3px 0 0 ${accent}, 0 12px 32px rgba(0,0,0,0.12), 0 0 0 1px ${accent}18`
          : darkMode
            ? `inset 3px 0 0 ${accent}, 0 4px 12px rgba(0,0,0,0.2)`
            : `inset 3px 0 0 ${accent}, 0 2px 8px rgba(0,0,0,0.07)`,
      }}
      onClick={handleCardClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Decorative blurred circle — top-right, WorkflowCard-style */}
      <div
        className="absolute top-0 right-0 w-24 h-24 rounded-full pointer-events-none
                   translate-x-10 -translate-y-10 transition-transform duration-500"
        style={{
          background: `radial-gradient(circle, ${accent}25 0%, transparent 70%)`,
          transform: hovered ? 'translate(40px, -40px) scale(1.5)' : 'translate(40px, -40px) scale(1)',
        }}
      />

      {/* ── Header band ─────────────────────────────────────────────── */}
      <div
        className="px-4 pt-3 pb-2.5"
        style={{ background: darkMode ? `${accent}12` : `${accent}09` }}
      >
        <div className="flex items-start justify-between gap-2">
          {/* Emoji badge + gradient name */}
          <div className="flex items-center gap-2.5 min-w-0">
            {/* Emoji in a tinted pill */}
            <div
              className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-base shadow-sm"
              style={{ background: `${accent}28` }}
            >
              {emoji}
            </div>

            <div className="min-w-0">
              {/* Gradient name — stolen from PersonaCard */}
              <h3
                className="font-bold text-sm leading-snug truncate bg-clip-text text-transparent"
                style={{ backgroundImage: `linear-gradient(to right, ${accent}, ${accentDark})` }}
              >
                {artifact.name}
              </h3>

              {/* Type chip */}
              <span
                className="inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full mt-0.5"
                style={{ background: `${accent}20`, color: accent }}
              >
                {typeLabel}
              </span>
            </div>
          </div>

          {/* Version badge — accent-outlined */}
          {artifact.version > 1 && (
            <span
              className="flex-shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded-full border mt-0.5"
              style={{ borderColor: accent, color: accent }}
            >
              v{artifact.version}
            </span>
          )}
        </div>
      </div>

      {/* ── Body ────────────────────────────────────────────────────── */}
      <div className="px-4 pt-2.5 pb-3">

        {/* ── Description block: all three layers ─────────────────── */}
        <div
          className="relative mb-3 pl-2.5 border-l-2 space-y-1.5"
          style={{ borderColor: `${accent}55` }}
        >
          {/* Layer 1 — type tagline, always shown as a muted baseline */}
          <p className={`text-[11px] font-semibold uppercase tracking-wide ${
            darkMode ? 'text-gray-500' : 'text-gray-400'
          }`}>
            {tagline}
          </p>

          {/* Layer 3 — explicit description from metadata.description */}
          {dbDescription && (
            <p className={`text-[12px] leading-relaxed line-clamp-2 font-medium ${
              darkMode ? 'text-gray-200' : 'text-gray-700'
            }`}>
              {dbDescription}
            </p>
          )}

          {/* Layer 2 — composed sentence from existing summary fields */}
          {composed && (
            <p className={`text-[11px] leading-relaxed ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {composed}
            </p>
          )}
        </div>

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2.5">
            {tags.slice(0, 3).map((tag, i) => (
              <span
                key={i}
                className="text-[11px] px-1.5 py-0.5 rounded-full font-medium"
                style={
                  i === 0
                    ? { background: `${accent}20`, color: accent }
                    : {
                        background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
                        color: darkMode ? '#9ca3af' : '#6b7280',
                      }
                }
              >
                {tag}
              </span>
            ))}
            {tags.length > 3 && (
              <span className={`text-[11px] px-1.5 py-0.5 rounded-full ${
                darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
              }`}>
                +{tags.length - 3}
              </span>
            )}
          </div>
        )}

        {/* Meta row: agent avatar + phase + timestamp */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            {artifact.agent_name && (
              <>
                <AgentAvatar name={artifact.agent_name} accent={accent} />
                <span className={`text-[11px] truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  {artifact.agent_name}
                </span>
              </>
            )}
            {artifact.phase && (
              <span
                className="text-[10px] px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ml-1"
                style={{ background: `${accent}18`, color: accent }}
              >
                {PHASE_LABEL[artifact.phase] ?? artifact.phase}
              </span>
            )}
          </div>

          <div className={`flex items-center gap-1 text-[11px] flex-shrink-0 ${
            darkMode ? 'text-gray-500' : 'text-gray-400'
          }`}>
            <FiClock className="w-3 h-3" />
            {relativeTime(artifact.created_at)}
          </div>
        </div>

        {/* Hover quick-actions — slide up */}
        <div
          className="flex items-center gap-2 mt-2.5 transition-all duration-200"
          style={{
            opacity: hovered ? 1 : 0,
            transform: hovered ? 'translateY(0)' : 'translateY(6px)',
            pointerEvents: hovered ? 'auto' : 'none',
          }}
        >
          <button
            onClick={handleCopy}
            className={`flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-lg font-medium transition-colors ${
              darkMode
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
            }`}
          >
            {copied ? <FiCheck className="w-3 h-3 text-green-400" /> : <FiCopy className="w-3 h-3" />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <button
            onClick={handleOpen}
            className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-lg font-medium text-white transition-all hover:opacity-90 hover:scale-105"
            style={{ background: `linear-gradient(to right, ${accent}, ${accentDark})` }}
          >
            <FiExternalLink className="w-3 h-3" />
            Open
          </button>
        </div>
      </div>
    </div>
  );
};
